import Commons from '../../../../Services/Common/Common';

/**
 * 작업환경측정 공정별 측정주기(WOR_BASIC_01) 스키마
 * - fields: 그리드에서 쓰는 필드 정의 (initial, label)
 * - mapper: 조회/저장 API payload 생성 규칙
 *   - search1: GetWorBasicR2 조회 파라미터 (mapParamsBySchema)
 *   - save1: SaveWorBasicU2 1건 payload (행×8건 확장 시 buildSaveWorBasicU2Requests에서 (row, context)로 호출)
 */
export const WOR_BASIC_01_Schema = {
  fields: {
    no: { initial: '', label: 'NO' },
    gongJongCd: { initial: '', label: '공정코드' },
    gongJongNm: { initial: '', label: '공정명' },
    year1FirstHalf: { initial: '', label: '상반기' },
    year1SecondHalf: { initial: '', label: '하반기' },
    year2FirstHalf: { initial: '', label: '상반기' },
    year2SecondHalf: { initial: '', label: '하반기' },
    year3FirstHalf: { initial: '', label: '상반기' },
    year3SecondHalf: { initial: '', label: '하반기' },
    year4FirstHalf: { initial: '', label: '상반기' },
    year4SecondHalf: { initial: '', label: '하반기' },
    remark: { initial: '', label: '비고' },
  },

  mapper: {
    /** GetWorBasicR2 조회 파라미터 (CustCd, BaseYear 암호화) */
    search1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      BaseYear: (_, context) =>
        Commons.EncryptAES(context.search?.baseYear || '') || '',
    },

    /** SaveWorBasicU2 1건 (row + context에 year, half, measureDt, isPass 담아서 호출) */
    save1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.custCd || '') || '',
      GongjongCd: (row) =>
        Commons.EncryptAES(row.gongJongCd || '') || '',
      MeasureYear: (_, context) =>
        Commons.EncryptAES(String(context.year ?? '')) || '',
      MeasureHalf: (_, context) =>
        Commons.EncryptAES(String(context.half ?? '')) || '',
      MeasureDt: (_, context) =>
        context.measureDt
          ? Commons.EncryptAES(context.measureDt) || ''
          : '',
      PassYn: (_, context) => {
        // PASS 값이 비어있는 경우(아예 선택 안 한 경우)는 빈값으로 저장
        if (context.isPass === null || context.isPass === undefined) return '';
        return Commons.EncryptAES(context.isPass ? 'Y' : 'N') || '';
      },
      Remark: (row) =>
        Commons.EncryptAES(row.remark || '') || '',
      UserId: (_, context) =>
        context.userInfo?.userid || '',
    },
  },
};

/**
 * 조회: 서버 데이터 → mapGetWorBasicR2RowToGrid → 그리드 표시 (암호화 해제 및 날짜 포맷 변경)
 * 서버 응답 한 행을 그리드 행(camelCase)으로 변환
 * - 서버: GongJongNm, Year1FirstHalf, ... (날짜는 'PASS' | 'yy.mm.dd' | '-')
 * - 그리드: gongJongNm, year1FirstHalf, ... (날짜는 'PASS' | 'YYYY-MM-DD' | '')
 * 
 */
export const mapGetWorBasicR2RowToGrid = (row) => {
  if (!row) return null;
  const toGridValue = (v) => {
    if (v == null || v === '' || v === '-') return '';
    if (v === 'PASS') return 'PASS';
    const m = String(v).match(/^(\d{2})\.(\d{2})\.(\d{2})$/);
    if (m) return `20${m[1]}-${m[2]}-${m[3]}`;
    return v;
  };
  const mapped = {
    gongJongCd: row.GongJongCd ?? row.gongJongCd ?? '',
    gongJongNm: row.GongJongNm ?? row.gongJongNm ?? '',
    year1FirstHalf: toGridValue(row.Year1FirstHalf ?? row.year1FirstHalf),
    year1SecondHalf: toGridValue(row.Year1SecondHalf ?? row.year1SecondHalf),
    year2FirstHalf: toGridValue(row.Year2FirstHalf ?? row.year2FirstHalf),
    year2SecondHalf: toGridValue(row.Year2SecondHalf ?? row.year2SecondHalf),
    year3FirstHalf: toGridValue(row.Year3FirstHalf ?? row.year3FirstHalf),
    year3SecondHalf: toGridValue(row.Year3SecondHalf ?? row.year3SecondHalf),
    year4FirstHalf: toGridValue(row.Year4FirstHalf ?? row.year4FirstHalf),
    year4SecondHalf: toGridValue(row.Year4SecondHalf ?? row.year4SecondHalf),
    remark: row.Remark ?? row.remark ?? '',
  };
  if (row.no != null) mapped.no = row.no;
  return mapped;
};

/**
 * (row, context)로 save1 mapper 적용해 SaveWorBasicU2 1건 payload 생성
 * - context: { custCd, baseYear, userId, year, half, measureDt, isPass }
 */
const mapOneSaveItem = (row, context, mapper) => {
  const payload = {};
  for (const key in mapper) {
    const fn = mapper[key];
    payload[key] = typeof fn === 'function' ? fn(row, context) : fn;
  }
  return payload;
};

/**
 * 수정된 그리드 행만 → SaveWorBasicU2 요청 배열 (TB_BS_MEASURE_CYCLE)
 * - 호출부에서 변경된 행만 넘기면 됨 (수정된 데이터만 저장)
 * - 행당 8건(4개년×상/하반기), mapper.save1으로 1건씩 생성
 * - 8건의 데이터로 분리해서 저장
 * @param {Array} rows - 수정된 행만 (changedRowData 기반으로 호출부에서 필터한 배열)
 * @param {object} context - { custCd, baseYear, userId }
 * @returns {Array<SaveWorBasicU2Info>}
 */
export const buildSaveWorBasicU2Requests = (rows, context) => {
  if (!rows?.length || !context?.custCd) return [];
  const baseYear = parseInt(context.baseYear, 10) || new Date().getFullYear();
  const year1 = baseYear - 2;
  const year2 = baseYear - 1;
  const year3 = baseYear;
  const year4 = baseYear + 1;

  const periodFields = [
    { key: 'year1FirstHalf', year: year1, half: 1 },
    { key: 'year1SecondHalf', year: year1, half: 2 },
    { key: 'year2FirstHalf', year: year2, half: 1 },
    { key: 'year2SecondHalf', year: year2, half: 2 },
    { key: 'year3FirstHalf', year: year3, half: 1 },
    { key: 'year3SecondHalf', year: year3, half: 2 },
    { key: 'year4FirstHalf', year: year4, half: 1 },
    { key: 'year4SecondHalf', year: year4, half: 2 },
  ];

  const requests = [];
  const save1Mapper = WOR_BASIC_01_Schema.mapper.save1;
  for (const row of rows) {
    for (const { key, year, half } of periodFields) {
      const value = row[key];

      let isPass = null;
      let measureDt = '';

      if (value === 'PASS') {
        // PASS 선택: PASS = Y, 날짜 없음
        isPass = true;
        measureDt = '';
      } else if (!value) {
        // 아무 값도 없을 때: PASS / 날짜 모두 빈값으로 저장
        isPass = null;
        measureDt = '';
      } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        // 날짜 선택: PASS = N, 해당 일자 저장
        isPass = false;
        measureDt = value;
      } else {
        // 그 외 값은 안전하게 빈값으로 처리
        isPass = null;
        measureDt = '';
      }

      const periodContext = {
        ...context,
        year,
        half,
        measureDt,
        isPass,
      };
      requests.push(mapOneSaveItem(row, periodContext, save1Mapper));
    }
  }
  return requests;
};
